/**
 * Home — as três fases, sobre a viagem REAL que está aberta.
 *
 * A fase é derivada das datas dessa viagem, na timezone dela. Nome, destino,
 * datas, duração e moeda vêm do servidor (`TripContext`); o conteúdo vem dos
 * endpoints da própria viagem. Não há dataset de exemplo neste caminho, e não
 * há valor de reserva: onde não existe dado, existe estado vazio com um
 * caminho para criar o primeiro.
 *
 * O painel de demonstração continua podendo mover o "hoje" e forçar estados de
 * tela — sobre estas datas, sem inventar conteúdo.
 */
import { formatCountdown, formatDayMonth } from '@nv/core';
import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';

import { useAgora, useDemo } from '../../demo/DemoContext';
import { useTrip } from '../../trip/TripContext';
import { tripInputDaViagem } from '../../trip/tripInput';
import { NotificacoesSheet, useNotificacoes } from '../notificacoes/NotificacoesSheet';
import { AniversariosNaHome } from '../aniversarios/AniversariosNaHome';
import { Icon } from '../../ui/icons';
import { ErrorState, OfflineBanner, Skeleton } from '../../ui/primitives';
import { emptyHomeData, type HomeData } from './data';
import { useHomeData } from './homeData';
import { buildTripHeader } from './model';
import { AvisoRapidoPeloMais } from '../encontro/AvisoRapido';
import { AfterPhase } from './phases/AfterPhase';
import { BeforePhase } from './phases/BeforePhase';
import { DuringPhase } from './phases/DuringPhase';

export function HomeScreen() {
  const navigate = useNavigate();
  const { trip, currency, members } = useTrip();
  const [notificacoesAbertas, setNotificacoesAbertas] = useState(false);
  // O ponto no sino só quando algo pede algo de mim — nunca um ponto fixo.
  const notificacoes = useNotificacoes(trip.id);
  const pedindo = notificacoes.data?.awaitingMe ?? 0;
  const viagem = tripInputDaViagem(trip, members.length);
  const { dataState, setDataState, offline } = useDemo();
  const agora = useAgora(viagem);
  const home = useHomeData(viagem, agora);

  // Uma viagem sem datas válidas não é desenhável. Só acontece se o servidor
  // devolver algo impossível — e "Invalid Date" na tela seria pior.
  if (!viagem) {
    return (
      <section className="screen home">
        <ErrorState
          title="Não foi possível abrir a viagem"
          description="Os dados desta viagem vieram incompletos. Escolha outra em Minhas viagens."
          onRetry={() => window.location.reload()}
        />
      </section>
    );
  }

  const header = buildTripHeader(viagem, agora);
  const carregando = dataState === 'loading' || home.carregando;
  const comErro = dataState === 'error' || home.erro;
  const data: HomeData = dataState === 'empty' ? emptyHomeData : home.data;


  return (
    <>
      {offline && <OfflineBanner />}

      <section className="screen home">
        {/* Um cabeçalho só, nas três fases: qual viagem, onde ela está no
            tempo, e o sino. A pessoa (foto, conta) vive no Mais (lote de
            aparência 1/2): um caminho só para a conta. */}
        <header className="home-header">
          {/* O nome da viagem abre Minhas viagens (PO, 19/09): de lá se troca
              de viagem, cria outra ou configura esta (engrenagem). */}
          <Link className="home-header__trip" to="/viagens" aria-label={`${header.name} — minhas viagens`} data-testid="home-viagem">
            <span className="home-header__lines">
              <span className="home-header__name">{header.name}</span>
              <span className="home-header__sub">
                {header.phase === 'durante' &&
                  `Dia ${header.dayNumber} de ${header.durationInDays}${home.hoje ? ` · ${formatDayMonth(home.hoje)}` : ''}`}
                {header.phase === 'antes' &&
                  (header.daysUntilStart !== null
                    ? `${formatCountdown(header.daysUntilStart)} · ${header.range}`
                    : header.range)}
                {header.phase === 'depois' && `Terminou · ${header.range}`}
              </span>
            </span>
          </Link>
          <button
            type="button"
            className="home-header__bell"
            aria-label={pedindo > 0 ? `Notificações — ${pedindo} aguardando você` : 'Notificações'}
            onClick={() => setNotificacoesAbertas(true)}
          >
            <Icon name="notificacoes" size={22} />
            {pedindo > 0 && <span className="home-header__badge" aria-hidden="true">{pedindo > 9 ? '9+' : pedindo}</span>}
          </button>
        </header>

        {notificacoesAbertas && (
          <NotificacoesSheet tripId={trip.id} onClose={() => setNotificacoesAbertas(false)} />
        )}

        {carregando && (
          <>
            <Skeleton height={180} raio="hero" />
            <Skeleton height={64} count={3} />
          </>
        )}

        {!carregando && comErro && (
          <ErrorState
            title="Não foi possível carregar"
            description="Mostrando a última versão salva no telefone. Alterações feitas agora sincronizam depois."
            onRetry={() => setDataState('ready')}
          />
        )}

        {!carregando && !comErro && (
          <>
            {header.phase !== 'durante' && <AniversariosNaHome hoje={home.hoje} />}
            {header.phase === 'antes' && <BeforePhase header={header} data={data} viagem={viagem} agora={agora} />}
            {header.phase === 'durante' && (
              <DuringPhase header={header} data={data} currency={currency} hoje={home.hoje} />
            )}
            {header.phase === 'depois' && (
              <AfterPhase header={header} data={data} currency={currency} />
            )}
          </>
        )}
        {/* A folha "Alertar o grupo", quando a pessoa chega pelo "+" (`/?aviso=1`):
            em qualquer fase — antes da viagem também se avisa (PO, 19/09). */}
        <AvisoRapidoPeloMais />
      </section>
    </>
  );
}
